import axios from "axios";
import { AppResponse } from "../models/common/app-response";
import { TokenDto } from "../models/auth/token.dto";
import { DateUtils } from "../helpers/dateUtils";
import { storageService } from "./storageService";

const refreshTokenApiUrl = "/api/v1/accounts/refresh-token";
const LOCK_NAME = "tmed-token-refresh";
const STORAGE_LOCK_KEY = "token-refresh-lock";
const AUTH_CHANNEL_NAME = "tmed-auth";
const STORAGE_LOCK_TTL_MS = 15_000;
const STORAGE_LOCK_WAIT_MS = 15_000;
const STORAGE_LOCK_POLL_MS = 100;

let refreshPromise: Promise<TokenDto> | null = null;
let onTokenRefreshed: (() => void) | null = null;
let syncInitialized = false;
let authChannel: BroadcastChannel | null = null;

export function setOnAccessTokenRefreshed(handler: (() => void) | null): void {
    onTokenRefreshed = handler;
}

export function hasValidSession(): boolean {
    const tokenData = storageService.getTokenData();
    return !!tokenData && DateUtils.isNotExpired(tokenData.refreshTokenExpiration);
}

export function isAccessTokenExpired(): boolean {
    const tokenData = storageService.getTokenData();
    if (!tokenData) {
        return true;
    }
    return !DateUtils.isNotExpired(tokenData.accessTokenExpiration);
}

function notifyTokenRefreshed(): void {
    onTokenRefreshed?.();
    try {
        authChannel?.postMessage({ type: "TOKEN_REFRESHED" });
    } catch {
        // BroadcastChannel may be closed or unsupported in some environments.
    }
}

function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

function tryAcquireStorageLock(): boolean {
    const now = Date.now();
    const raw = localStorage.getItem(STORAGE_LOCK_KEY);
    if (raw) {
        const lockedAt = Number(raw);
        if (Number.isFinite(lockedAt) && now - lockedAt < STORAGE_LOCK_TTL_MS) {
            return false;
        }
    }
    localStorage.setItem(STORAGE_LOCK_KEY, String(now));
    const confirm = localStorage.getItem(STORAGE_LOCK_KEY);
    return confirm === String(now);
}

function releaseStorageLock(): void {
    localStorage.removeItem(STORAGE_LOCK_KEY);
}

async function waitForStorageLockRelease(timeoutMs: number): Promise<void> {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
        const raw = localStorage.getItem(STORAGE_LOCK_KEY);
        if (!raw) {
            return;
        }
        const lockedAt = Number(raw);
        if (!Number.isFinite(lockedAt) || Date.now() - lockedAt >= STORAGE_LOCK_TTL_MS) {
            releaseStorageLock();
            return;
        }
        await sleep(STORAGE_LOCK_POLL_MS);
    }
}

/**
 * After waiting for another tab's refresh, return tokens if they were updated.
 */
function getTokenIfRefreshed(previous: TokenDto | null): TokenDto | null {
    const current = storageService.getTokenData();
    if (!current) {
        return null;
    }
    if (!previous) {
        return !isAccessTokenExpired() ? current : null;
    }
    if (
        current.accessToken !== previous.accessToken ||
        current.refreshToken !== previous.refreshToken
    ) {
        return current;
    }
    if (!isAccessTokenExpired()) {
        return current;
    }
    return null;
}

async function performRefreshRequest(refreshToken: string): Promise<TokenDto> {
    const response = await axios.post<AppResponse<TokenDto>>(
        `${window.__env.apiUrl}${refreshTokenApiUrl}`,
        { refreshToken },
        { headers: { "Content-Type": "application/json" } }
    );

    if (!response.data.success || !response.data.data) {
        throw new Error(response.data.message || "Refresh failed");
    }

    storageService.setTokenData(response.data.data);
    notifyTokenRefreshed();
    return response.data.data;
}

async function refreshUnderLock(previous: TokenDto | null): Promise<TokenDto> {
    const alreadyRefreshed = getTokenIfRefreshed(previous);
    if (alreadyRefreshed) {
        notifyTokenRefreshed();
        return alreadyRefreshed;
    }

    const refreshToken = storageService.getTokenData()?.refreshToken;
    if (!refreshToken) {
        throw new Error("No refresh token");
    }

    return performRefreshRequest(refreshToken);
}

async function withCrossTabLock<T>(fn: () => Promise<T>): Promise<T> {
    const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
    if (locks?.request) {
        return locks.request(LOCK_NAME, fn);
    }

    const deadline = Date.now() + STORAGE_LOCK_WAIT_MS;
    while (Date.now() < deadline) {
        if (tryAcquireStorageLock()) {
            try {
                return await fn();
            } finally {
                releaseStorageLock();
            }
        }
        await waitForStorageLockRelease(STORAGE_LOCK_POLL_MS * 5);
        await sleep(STORAGE_LOCK_POLL_MS);
    }

    // Last resort: proceed without exclusive lock (backend grace covers the race).
    return fn();
}

export async function refreshAccessToken(): Promise<TokenDto> {
    if (refreshPromise) {
        return refreshPromise;
    }

    const previous = storageService.getTokenData();
    if (!previous?.refreshToken) {
        throw new Error("No refresh token");
    }

    refreshPromise = withCrossTabLock(() => refreshUnderLock(previous))
        .finally(() => {
            refreshPromise = null;
        });

    return refreshPromise;
}

/**
 * Subscribe once to cross-tab token updates (storage + BroadcastChannel).
 */
export function initTokenRefreshSync(): void {
    if (syncInitialized || typeof window === "undefined") {
        return;
    }
    syncInitialized = true;

    window.addEventListener("storage", (event) => {
        if (event.key === "token" && event.newValue) {
            notifyTokenRefreshed();
        }
    });

    if (typeof BroadcastChannel !== "undefined") {
        try {
            authChannel = new BroadcastChannel(AUTH_CHANNEL_NAME);
            authChannel.onmessage = (event) => {
                if (event.data?.type === "TOKEN_REFRESHED") {
                    onTokenRefreshed?.();
                }
            };
        } catch {
            authChannel = null;
        }
    }
}

initTokenRefreshSync();
